import React, { useRef } from 'react';
import { Alert, AlertTitle, Box, Button, Checkbox, Divider, FormControlLabel, TextField, Typography } from '@mui/material';
import { DefinitionList, Sheet, StatusPill, Tag } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { MISSING, formatCount, formatDateTime, formatDays, formatNumber } from '../../../utils/format';
import CheckpointMark from './CheckpointMark';
import {
  checkpointMeta,
  firstName,
  isUncappedHolidayShiftKey,
  repStatus,
  suspensionEndDate,
  tidyRange,
} from './helpers';
import type { WeekendShiftAvailability, WeekendShiftRosterData, WeekendShiftRosterRow } from './types';

const REASON_LIMIT = 500;

export interface RepSheetProps {
  rep: WeekendShiftRosterRow | null;
  rosterData: WeekendShiftRosterData | null;
  weekendLabel: string;
  availabilityByKey: Map<string, WeekendShiftAvailability>;
  /** The viewed weekend is over: show what happened, not live capacity. */
  isPastWeekend: boolean;
  overCap: boolean;
  draftShiftKeys: string[];
  onToggleShift: (shiftKey: string) => void;
  onClearDraft: () => void;
  suspensionReason: string;
  onSuspensionReasonChange: (value: string) => void;
  savingAssignment: boolean;
  applyingSuspension: boolean;
  clearingSuspension: boolean;
  saveDisabled: boolean;
  onClose: () => void;
  onSave: () => void;
  onRequestSuspend: () => void;
  onRequestUnsuspend: () => void;
}

/**
 * One rep's weekend: assign or remove shifts (admins can save after the
 * signup deadline), see each shift's check-ins, and suspend or reinstate
 * signup access.
 */
const RepSheet: React.FC<RepSheetProps> = ({
  rep,
  rosterData,
  weekendLabel,
  availabilityByKey,
  isPastWeekend,
  overCap,
  draftShiftKeys,
  onToggleShift,
  onClearDraft,
  suspensionReason,
  onSuspensionReasonChange,
  savingAssignment,
  applyingSuspension,
  clearingSuspension,
  saveDisabled,
  onClose,
  onSave,
  onRequestSuspend,
  onRequestUnsuspend,
}) => {
  // Keep the last rep on screen while the sheet animates closed.
  const lastRep = useRef<WeekendShiftRosterRow | null>(rep);
  if (rep) lastRep.current = rep;
  const shown = rep ?? lastRep.current;

  const busy = savingAssignment || applyingSuspension || clearingSuspension;
  const suspended = Boolean(shown?.suspensionStatus?.isSuspended);
  const cap = rosterData?.standardShiftCap ?? 2;
  const standardDraftCount = draftShiftKeys.filter((key) => !isUncappedHolidayShiftKey(key)).length;
  const removingAll = draftShiftKeys.length === 0;
  const hadShifts = (shown?.selectedShiftKeys.length ?? 0) > 0;
  const name = shown ? firstName(shown.name) : '';

  const disabledReason = !saveDisabled || busy
    ? null
    : suspended
      ? 'Lift the suspension to assign shifts.'
      : standardDraftCount > cap
        ? `Choose at most ${cap} Saturday or Sunday shifts.`
        : null;

  const detailsByKey = new Map((shown?.selectedShiftDetails ?? []).map((detail) => [detail.key, detail]));
  const status = shown ? repStatus(shown, overCap) : null;

  return (
    <Sheet
      open={Boolean(rep)}
      onClose={onClose}
      title={shown?.name ?? ''}
      subtitle={shown ? `${shown.email} · ${shown.teamName || 'Sales'}` : undefined}
      actions={
        shown ? (
          <>
            {disabledReason && (
              <Typography variant="body2" sx={{ color: sbg.text.secondary, mr: 'auto', alignSelf: 'center' }}>
                {disabledReason}
              </Typography>
            )}
            <Button variant="text" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="contained" onClick={onSave} disabled={saveDisabled} aria-busy={savingAssignment || undefined}>
              {savingAssignment
                ? 'Saving…'
                : removingAll
                  ? 'Remove shifts'
                  : `Save ${formatCount(draftShiftKeys.length, 'shift')}`}
            </Button>
          </>
        ) : undefined
      }
    >
      {shown && rosterData && (
        <>
          <DefinitionList
            items={[
              { label: 'Weekend', value: weekendLabel },
              {
                label: 'Status',
                value: status && status.pills.length > 0 ? (
                  <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {status.pills.map((pill) => (
                      <StatusPill key={pill.label} tone={pill.tone}>{pill.label}</StatusPill>
                    ))}
                  </Box>
                ) : (
                  status?.summary ?? MISSING
                ),
              },
              { label: 'Submitted', value: shown.submittedAt ? formatDateTime(shown.submittedAt) : MISSING },
            ]}
          />

          {suspended && (
            <Alert severity="error">
              <AlertTitle>{`Suspended through ${suspensionEndDate(shown)}`}</AlertTitle>
              {`${name} can’t sign up for or be assigned weekend shifts for ${formatDays(shown.suspensionStatus.daysRemaining, 0)}.`}
              {shown.suspensionStatus.lastViolationReason && (
                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                  {`Reason: ${shown.suspensionStatus.lastViolationReason}`}
                </Box>
              )}
              {shown.selectedShiftKeys.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  sx={{ mt: 1.5 }}
                  onClick={onClearDraft}
                  disabled={busy || removingAll}
                >
                  Clear assigned shifts
                </Button>
              )}
            </Alert>
          )}

          <Box component="section" aria-labelledby="rep-sheet-shifts" sx={{ display: 'grid', gap: 1.5 }}>
            <Box>
              <Typography id="rep-sheet-shifts" variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
                Shifts
              </Typography>
              <Typography variant="body2" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
                {isPastWeekend
                  ? `This weekend is over. Each shift shows how ${name}’s check-ins went.`
                  : `You can assign after the signup deadline. Full shifts and the ${cap}-shift Saturday and Sunday cap still apply; holiday shifts have no cap.`}
              </Typography>
            </Box>

            {removingAll && hadShifts && (
              <Alert severity="warning">
                {`No shifts selected. Saving removes ${name} from ${weekendLabel}.`}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gap: 1 }}>
              {rosterData.shiftOptions.map((option) => {
                const availability = availabilityByKey.get(option.key);
                const isSelected = draftShiftKeys.includes(option.key);
                const isStandardShift = !isUncappedHolidayShiftKey(option.key);
                const shiftFull = Boolean(availability?.isFull && !isSelected);
                // Holiday shifts are uncapped extras and never trip the per-rep cap.
                const standardCapReached = isStandardShift && !isSelected && standardDraftCount >= rosterData.standardShiftCap;
                const disabled = suspended || shiftFull || standardCapReached || savingAssignment || applyingSuspension;
                const capacityLine = !availability
                  ? 'Availability didn’t load'
                  : isPastWeekend
                    ? `${formatCount(availability.count, 'rep')} signed up`
                    : availability.uncapped
                      ? `${formatCount(availability.count, 'rep')} signed up · no cap`
                      : `${formatNumber(availability.count)} of ${formatNumber(availability.capacity)} signed up · ${
                          availability.isFull ? 'Full' : `${formatCount(availability.remaining, 'spot')} left`
                        }`;
                // A suspension is the reason when the rep is suspended; the cap only matters on a live weekend.
                const showCapReason = standardCapReached && !suspended && !isPastWeekend;
                const detail = detailsByKey.get(option.key);

                return (
                  <Box
                    key={option.key}
                    sx={{
                      border: `1px solid ${isSelected ? sbg.border.selected : sbg.border.default}`,
                      bgcolor: isSelected ? sbg.bg.selected : sbg.bg.surface,
                      borderRadius: sbg.radius.lg,
                      px: 1.5,
                      py: 1.25,
                      transition: `background-color ${sbg.duration.fast} ${sbg.ease.standard}, border-color ${sbg.duration.fast} ${sbg.ease.standard}`,
                    }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, width: '100%', alignItems: 'flex-start', gap: 1 }}
                      control={
                        <Checkbox
                          checked={isSelected}
                          disabled={disabled}
                          onChange={() => onToggleShift(option.key)}
                          sx={{ mt: '-2px' }}
                        />
                      }
                      label={
                        <Box sx={{ minWidth: 0 }}>
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="labelMd" component="span" sx={{ color: sbg.text.primary }}>
                              {tidyRange(option.shortLabel)}
                            </Typography>
                            {!isStandardShift && <Tag sx={{ height: 20, px: 1 }}>Holiday</Tag>}
                          </Box>
                          <Typography variant="body2" component="span" sx={{ display: 'block', color: sbg.text.secondary }}>
                            {tidyRange(option.fullLabel)}
                          </Typography>
                          <Typography variant="body2" component="span" sx={{ display: 'block', color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                            {capacityLine}
                            {showCapReason ? ` · ${rosterData.standardShiftCap}-shift Saturday and Sunday cap reached` : ''}
                          </Typography>
                        </Box>
                      }
                    />
                    {detail && (
                      <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 1, p: 0, pl: 3.5, display: 'grid', gap: 0.75 }}>
                        {(['start', 'end'] as const).map((phase) => {
                          const checkpoint = phase === 'start' ? detail.startCheckin : detail.endCheckin;
                          if (checkpoint.checkinStatus === 'scheduled') {
                            // Not open yet: the API's sentence says when it opens.
                            return (
                              <Box component="li" key={phase}>
                                <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
                                  {checkpoint.checkinStatusReason}
                                </Typography>
                              </Box>
                            );
                          }
                          return (
                            <Box component="li" key={phase} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                              <CheckpointMark phase={phase} checkpoint={checkpoint} showTime={false} />
                              <Typography variant="body2" sx={{ color: sbg.text.secondary, pt: '2px', minWidth: 0 }}>
                                {checkpointMeta(checkpoint)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider />

          <Box component="section" aria-labelledby="rep-sheet-access" sx={{ display: 'grid', gap: 1.5 }}>
            <Box>
              <Typography id="rep-sheet-access" variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
                Signup access
              </Typography>
              <Typography variant="body2" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
                {suspended
                  ? `Lifting the suspension lets ${name} sign up for weekend shifts again right away.`
                  : `Suspending blocks ${name} from new weekend signups for 60 days. Shifts already assigned stay until you remove them.`}
              </Typography>
            </Box>
            {!suspended && (
              <TextField
                label="Suspension reason (optional)"
                value={suspensionReason}
                onChange={(event) => onSuspensionReasonChange(event.target.value.slice(0, REASON_LIMIT))}
                multiline
                minRows={3}
                maxRows={8}
                helperText={`Kept in the admin record; ${name} sees a neutral message. ${formatNumber(suspensionReason.length)}/${formatNumber(REASON_LIMIT)} characters.`}
                fullWidth
              />
            )}
            <Box>
              {suspended ? (
                <Button variant="outlined" onClick={onRequestUnsuspend} disabled={busy} aria-busy={clearingSuspension || undefined}>
                  {clearingSuspension ? 'Lifting suspension…' : 'Lift suspension'}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onRequestSuspend}
                  disabled={busy}
                  aria-busy={applyingSuspension || undefined}
                >
                  {applyingSuspension ? 'Suspending…' : 'Suspend for 60 days'}
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
    </Sheet>
  );
};

export default RepSheet;
